import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useGetMySessions, useConfirmSession, useRejectSession, getGetMySessionsQueryKey } from "@workspace/api-client-react";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format, differenceInDays, startOfWeek, endOfWeek, eachDayOfInterval, getDay } from "date-fns";
import { Calendar, Clock, Check, X, DollarSign, Users, Star, TrendingUp, Download, Loader2, Package, AlertTriangle, BarChart2 } from "lucide-react";
import { Link } from "wouter";
import { GoogleCalendarSync } from "@/components/google-calendar-sync";
import { apiFetch } from "@/lib/api";
import { PayoutBreakdown } from "@/components/payout-breakdown";

interface AnnotatedSlot {
  start: string;
  end: string;
  trainerId: string;
  locationId?: string;
  isAvailable: boolean;
}

interface StudentCreditPackage {
  purchaseId: string;
  packageName: string;
  creditsRemaining: number;
  creditsTotal: number;
  expiresAt: string | null;
  purchasedAt: string;
}

interface StudentCredit {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  totalCreditsRemaining: number;
  packages: StudentCreditPackage[];
}

export default function TrainerDashboard() {
  const { user } = useAuth();
  const { data: sessions, isLoading } = useGetMySessions();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const confirmMutation = useConfirmSession({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetMySessionsQueryKey() });
        toast({ title: "Session accepted", description: "The session has been confirmed." });
      },
      onError: () => {
        toast({ title: "Failed to accept", description: "Something went wrong. Please try again.", variant: "destructive" });
      },
    },
  });

  const rejectMutation = useRejectSession({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetMySessionsQueryKey() });
        toast({ title: "Session declined", description: "The session has been declined." });
      },
      onError: () => {
        toast({ title: "Failed to decline", description: "Something went wrong. Please try again.", variant: "destructive" });
      },
    },
  });

  const pending = sessions?.filter(s => s.status === "PENDING") || [];
  const upcoming = sessions?.filter(s => s.status === "CONFIRMED" && new Date(s.proposedStart) > new Date()) || [];
  const completed = sessions?.filter(s => s.status === "COMPLETED") || [];
  const totalEarned = completed.reduce((sum, s) => sum + (s.trainerPayout || 0), 0);

  const { data: studentCredits } = useQuery({
    queryKey: ["trainer", "student-credits"],
    queryFn: async () => {
      const res = await fetch("/api/trainers/my/student-credits", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch student credits");
      return res.json() as Promise<StudentCredit[]>;
    },
  });

  const { data: weekSlots } = useQuery<AnnotatedSlot[]>({
    queryKey: ["trainer-week-slots", user?.id],
    queryFn: () => apiFetch<AnnotatedSlot[]>(`/availability/all-slots/${user!.id}?daysAhead=7`),
    enabled: !!user?.id,
  });

  const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const weekDays = eachDayOfInterval({ start: startOfWeek(new Date()), end: endOfWeek(new Date()) });

  const slotsByDow = (weekSlots || []).reduce((acc, slot) => {
    const dow = getDay(new Date(slot.start));
    if (!acc[dow]) acc[dow] = { total: 0, booked: 0 };
    acc[dow].total++;
    if (!slot.isAvailable) acc[dow].booked++;
    return acc;
  }, {} as Record<number, { total: number; booked: number }>);

  const weekTotal = (weekSlots || []).length;
  const weekBooked = (weekSlots || []).filter(s => !s.isAvailable).length;
  const weekOpen = weekTotal - weekBooked;
  const utilisationPct = weekTotal > 0 ? Math.round((weekBooked / weekTotal) * 100) : 0;

  function utilisationColor(pct: number) {
    if (pct >= 80) return "text-green-400";
    if (pct >= 50) return "text-primary";
    if (pct >= 25) return "text-amber-400";
    return "text-white/50";
  }

  function utilisationBarColor(pct: number) {
    if (pct >= 80) return "bg-green-400";
    if (pct >= 50) return "bg-primary";
    if (pct >= 25) return "bg-amber-400";
    return "bg-white/20";
  }

  // For each student find their nearest expiry, then sort ascending and take top 3
  const expiringClients = (studentCredits || [])
    .map(s => {
      const nearest = s.packages
        .filter(p => p.expiresAt !== null)
        .map(p => ({ days: differenceInDays(new Date(p.expiresAt!), new Date()), pkg: p }))
        .sort((a, b) => a.days - b.days)[0] ?? null;
      return { ...s, nearestExpiry: nearest };
    })
    .filter(s => s.nearestExpiry !== null && s.nearestExpiry.days >= 0)
    .sort((a, b) => a.nearestExpiry!.days - b.nearestExpiry!.days)
    .slice(0, 3);

  function expiryColor(days: number): string {
    if (days <= 3) return "text-red-400";
    if (days <= 7) return "text-orange-400";
    if (days <= 14) return "text-yellow-400";
    return "text-green-400";
  }

  function expiryBg(days: number): string {
    if (days <= 3) return "bg-red-500/10 border-red-500/20";
    if (days <= 7) return "bg-orange-500/10 border-orange-500/20";
    if (days <= 14) return "bg-yellow-500/10 border-yellow-500/20";
    return "bg-green-500/10 border-green-500/20";
  }

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Coach Dashboard</h1>
          <p className="text-muted-foreground text-lg">Welcome back, {user?.firstName}</p>
        </div>

        <div className="flex gap-3">
          <a
            href={`${import.meta.env.VITE_API_URL || ""}/api/calendar/export.ics`}
            onClick={(e) => {
              e.preventDefault();
              const token = localStorage.getItem("token");
              fetch(`${import.meta.env.VITE_API_URL || ""}/api/calendar/export.ics`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              })
                .then((res) => res.blob())
                .then((blob) => {
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "openmat-sessions.ics";
                  a.click();
                  URL.revokeObjectURL(url);
                });
            }}
          >
            <Button variant="outline" size="sm" className="border-white/10 hover:bg-white/5">
              <Download className="w-4 h-4 mr-2" /> Export Calendar (.ics)
            </Button>
          </a>
          <GoogleCalendarSync />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Pending</CardDescription>
              <CardTitle className="text-4xl font-mono text-primary">{pending.length}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">Awaiting confirmation</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Upcoming</CardDescription>
              <CardTitle className="text-4xl font-mono text-white">{upcoming.length}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">Confirmed sessions</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Completed</CardDescription>
              <CardTitle className="text-4xl font-mono text-green-400">{completed.length}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">Total sessions</p></CardContent>
          </Card>
          <Card className="glass-panel border-white/5">
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Earnings</CardDescription>
              <CardTitle className="text-4xl font-mono text-primary">${totalEarned.toFixed(0)}</CardTitle>
            </CardHeader>
            <CardContent><p className="text-xs text-muted-foreground">Lifetime payout</p></CardContent>
          </Card>
        </div>

        {/* ── WEEKLY UTILISATION WIDGET ── */}
        {weekSlots !== undefined && (
          <Card className="glass-panel border-white/5">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                {/* Left: headline number */}
                <div className="shrink-0 text-center sm:text-left sm:border-r sm:border-white/10 sm:pr-6">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1">
                    <BarChart2 className="w-3.5 h-3.5" />
                    This Week's Utilisation
                  </div>
                  {weekTotal === 0 ? (
                    <p className="text-2xl font-mono text-white/40 mt-1">No schedule set</p>
                  ) : (
                    <>
                      <p className={`text-5xl font-mono font-bold ${utilisationColor(utilisationPct)}`}>
                        {utilisationPct}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {weekBooked} booked · {weekOpen} open · {weekTotal} total
                      </p>
                    </>
                  )}
                </div>

                {/* Right: per-day breakdown */}
                {weekTotal > 0 && (
                  <div className="flex-1 space-y-2.5">
                    {weekDays.map(day => {
                      const dow = getDay(day);
                      const dayData = slotsByDow[dow];
                      if (!dayData) return (
                        <div key={dow} className="flex items-center gap-3">
                          <span className="w-8 text-xs text-muted-foreground shrink-0">{DOW_LABELS[dow]}</span>
                          <div className="flex-1 h-2 rounded-full bg-white/5" />
                          <span className="text-xs text-white/20 w-14 text-right">no slots</span>
                        </div>
                      );
                      const dayPct = dayData.total > 0 ? (dayData.booked / dayData.total) * 100 : 0;
                      const isToday = getDay(new Date()) === dow;
                      return (
                        <div key={dow} className="flex items-center gap-3">
                          <span className={`w-8 text-xs shrink-0 font-medium ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                            {DOW_LABELS[dow]}
                          </span>
                          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${utilisationBarColor(dayPct)}`}
                              style={{ width: `${dayPct}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-14 text-right">
                            {dayData.booked}/{dayData.total} booked
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Global progress bar */}
              {weekTotal > 0 && (
                <div className="mt-5 pt-4 border-t border-white/5">
                  <div className="flex justify-between text-[11px] text-muted-foreground mb-1.5">
                    <span>0%</span>
                    <span className="text-primary font-medium">
                      {utilisationPct >= 80 ? "Fully packed — great week!" :
                       utilisationPct >= 50 ? "Good momentum this week" :
                       utilisationPct > 0 ? "Slots still available" :
                       "Nothing booked yet"}
                    </span>
                    <span>100%</span>
                  </div>
                  <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${utilisationBarColor(utilisationPct)}`}
                      style={{ width: `${utilisationPct}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <h2 className="text-2xl font-display font-bold text-white">Pending Requests</h2>
            </div>
            {pending.length > 0 ? pending.map(session => (
              <div key={session.id} className="p-5 rounded-2xl bg-card border border-primary/20 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center text-xl font-bold text-white shrink-0">
                    {(session as any).student?.firstName?.[0] || "?"}
                  </div>
                  <div>
                    <h4 className="font-bold text-white text-lg">{(session as any).student?.firstName} {(session as any).student?.lastName}</h4>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {format(new Date(session.proposedStart), "MMM d")}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {format(new Date(session.proposedStart), "h:mm a")}</span>
                    </div>
                    <div className="flex items-center gap-1.5 mt-1 text-sm">
                      <span className="text-muted-foreground">Your payout:</span>
                      <PayoutBreakdown session={session} />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30" disabled={confirmMutation.isPending || rejectMutation.isPending} onClick={() => confirmMutation.mutate({ id: session.id })}>{confirmMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Accept</Button>
                  <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" disabled={confirmMutation.isPending || rejectMutation.isPending} onClick={() => rejectMutation.mutate({ id: session.id, data: {} })}>{rejectMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <X className="w-4 h-4 mr-1" />} Decline</Button>
                </div>
              </div>
            )) : (
              <div className="p-8 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
                <Check className="w-10 h-10 text-green-400 mx-auto mb-3 opacity-50" />
                <p className="text-white font-medium">All caught up!</p>
                <p className="text-sm text-muted-foreground mt-1">No pending requests right now.</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <h2 className="text-2xl font-display font-bold text-white">Today's Schedule</h2>
              <Link href="/trainer/sessions"><span className="text-sm text-primary hover:underline cursor-pointer">Full Calendar</span></Link>
            </div>
            {upcoming.length > 0 ? upcoming.slice(0, 4).map(session => (
              <div key={session.id} className="p-4 rounded-xl bg-card border border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white">{(session as any).student?.firstName?.[0] || "?"}</div>
                  <div>
                    <p className="font-semibold text-white">{(session as any).student?.firstName} {(session as any).student?.lastName}</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(session.proposedStart), "h:mm a")} - {format(new Date(session.proposedEnd), "h:mm a")}</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-lg bg-green-500/10 text-green-400 border border-green-500/20">Confirmed</span>
              </div>
            )) : (
              <div className="p-8 text-center rounded-2xl border border-dashed border-white/10 bg-white/5">
                <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-white font-medium">No upcoming sessions</p>
                <p className="text-sm text-muted-foreground mt-1">New bookings will appear here.</p>
              </div>
            )}
          </div>
        </div>

        {/* ── EXPIRING CREDITS WIDGET ── */}
        {expiringClients.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-4">
              <div>
                <h2 className="text-2xl font-display font-bold text-white">Credits Expiring Soon</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Clients with active credits nearing their expiry date
                </p>
              </div>
              <Link href="/trainer/clients">
                <span className="text-sm text-primary hover:underline cursor-pointer">View all credits</span>
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {expiringClients.map(s => {
                const { days, pkg } = s.nearestExpiry!;
                return (
                  <div
                    key={s.studentId}
                    className={`p-4 rounded-xl border flex flex-col gap-3 ${expiryBg(days)}`}
                  >
                    {/* Student */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-sm font-bold text-white shrink-0">
                        {s.firstName[0]}{s.lastName[0]}
                      </div>
                      <div className="min-w-0">
                        <p className="font-bold text-white truncate">{s.firstName} {s.lastName}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{s.email}</p>
                      </div>
                    </div>

                    {/* Package name */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Package className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{pkg.packageName}</span>
                    </div>

                    {/* Credits bar */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-muted-foreground">Credits remaining</span>
                        <span className="text-xs font-semibold text-white">
                          {pkg.creditsRemaining}/{pkg.creditsTotal}
                        </span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary rounded-full"
                          style={{ width: `${(pkg.creditsRemaining / pkg.creditsTotal) * 100}%` }}
                        />
                      </div>
                    </div>

                    {/* Expiry countdown */}
                    <div className={`flex items-center gap-1.5 text-sm font-semibold ${expiryColor(days)}`}>
                      {days <= 7
                        ? <AlertTriangle className="w-4 h-4 shrink-0" />
                        : <Clock className="w-4 h-4 shrink-0" />
                      }
                      <span>
                        {days === 0 ? "Expires today" : `${days} day${days !== 1 ? "s" : ""} left`}
                      </span>
                      <span className="ml-auto text-[11px] font-normal text-muted-foreground">
                        {format(new Date(pkg.expiresAt!), "MMM d")}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
